/**
 * SkyComponent - procedural atmospheric sky rendering using Three.js Sky/SkyMesh.
 * Supports both WebGL (Sky) and WebGPU (SkyMesh) renderers with configurable
 * atmospheric scattering parameters.
 */

import * as THREE from 'three';
import { Sky } from 'three/addons/objects/Sky.js';
import { SkyMesh } from 'three/addons/objects/SkyMesh.js';
import { CubeRenderTarget } from 'three/webgpu';


import { GenesysScene } from '../../render/GenesysScene.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { callable, property } from '../../utils/serialization/decorator.js';
import { DirectionalLightComponent } from '../lights/DirectionalLightComponent.js';
import { SceneComponent } from '../SceneComponent.js';

import type { ISceneEnvironmentSource, SceneEnvironmentData } from '../../interfaces.js';
import type { SceneComponentOptions } from '../SceneComponent.js';


export interface SkyComponentOptions extends SceneComponentOptions {
  /** Turbidity - haziness of the atmosphere (0-20) */
  turbidity?: number;
  /** Rayleigh scattering - sky color intensity (0-4) */
  rayleigh?: number;
  /** Mie scattering coefficient - aerosol density (0-0.1) */
  mieCoefficient?: number;
  /** Mie directional g - scattering directionality (0-1) */
  mieDirectionalG?: number;
  /** Sun position vector */
  sunPosition?: THREE.Vector3;
  /** Cloud scale - size of cloud patterns (default: 0.0002) */
  cloudScale?: number;
  /** Cloud speed - animation speed (default: 0.0001) */
  cloudSpeed?: number;
  /** Cloud coverage - amount of sky covered (0-1, default: 0.4) */
  cloudCoverage?: number;
  /** Cloud density - opacity of clouds (0-1, default: 0.4) */
  cloudDensity?: number;
  /** Cloud elevation - height of cloud layer (0-1, default: 0.5) */
  cloudElevation?: number;
  /** Show sun disc - toggle sun rendering (default: true) */
  showSunDisc?: boolean;
  /** Time - cloud animation offset (default: 0) */
  time?: number;
  /** Whether to generate environment map from sky and apply it to the scene (default: false) */
  generateEnvironmentMap?: boolean;
  /** Intensity of the environment map (default: 1.0) */
  environmentMapIntensity?: number;
}

/** Procedural atmospheric sky component supporting WebGL and WebGPU. */
@EngineClass('SkyComponent')
export class SkyComponent extends SceneComponent implements ISceneEnvironmentSource {
  private skyInstance: Sky | SkyMesh | null = null;

  private _turbidity: number = 2;
  private _rayleigh: number = 1;
  private _mieCoefficient: number = 0.005;
  private _mieDirectionalG: number = 0.8;
  private _sunPosition: THREE.Vector3 = new THREE.Vector3(0, 1, 0);
  private _cloudScale: number = 0.0002;
  private _cloudSpeed: number = 0.0001;
  private _cloudCoverage: number = 0.4;
  private _cloudDensity: number = 0.4;
  private _cloudElevation: number = 0.5;
  private _showSunDisc: boolean = true;
  private _sunLight: WeakRef<DirectionalLightComponent> | null = null;
  private _generateEnvironmentMap: boolean = false;
  private _environmentMapIntensity: number = 1.0;

  private environmentRenderTarget: THREE.WebGLCubeRenderTarget | CubeRenderTarget | null = null;
  private environmentCamera: THREE.CubeCamera | null = null;
  private debugEnvMapSphere: THREE.Mesh | null = null;

  @property({ type: 'number', min: 0, max: 20, step: 0.1, description: 'Atmospheric turbidity (haziness)' })
  get turbidity(): number {
    return this._turbidity;
  }
  set turbidity(value: number) {
    if (value === this._turbidity) return;
    this._turbidity = value;
    this.updateSkyProperties();
  }

  @property({ type: 'number', min: 0, max: 4, step: 0.1, description: 'Rayleigh scattering intensity' })
  get rayleigh(): number {
    return this._rayleigh;
  }
  set rayleigh(value: number) {
    if (value === this._rayleigh) return;
    this._rayleigh = value;
    this.updateSkyProperties();
  }

  @property({ type: 'number', min: 0, max: 0.1, step: 0.001, description: 'Mie scattering coefficient' })
  get mieCoefficient(): number {
    return this._mieCoefficient;
  }
  set mieCoefficient(value: number) {
    if (value === this._mieCoefficient) return;
    this._mieCoefficient = value;
    this.updateSkyProperties();
  }

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Mie directional g (scattering directionality)' })
  get mieDirectionalG(): number {
    return this._mieDirectionalG;
  }
  set mieDirectionalG(value: number) {
    if (value === this._mieDirectionalG) return;
    this._mieDirectionalG = value;
    this.updateSkyProperties();
  }

  @property({ type: 'vector3', description: 'Sun position direction, ignored when there is a sun light in the scene' })
  get sunPosition(): THREE.Vector3 {
    return this._sunPosition;
  }
  set sunPosition(value: THREE.Vector3) {
    if (this._sunPosition.equals(value)) return;
    this._sunPosition.copy(value);
    this.updateSkyProperties();
  }

  @property({ type: 'number', min: 0.00001, max: 0.001, step: 0.00001, description: 'Cloud pattern scale' })
  get cloudScale(): number {
    return this._cloudScale;
  }
  set cloudScale(value: number) {
    if (value === this._cloudScale) return;
    this._cloudScale = value;
    this.updateSkyProperties();
  }

  @property({ type: 'number', min: 0, max: 0.01, step: 0.0001, description: 'Cloud animation speed' })
  get cloudSpeed(): number {
    return this._cloudSpeed;
  }
  set cloudSpeed(value: number) {
    if (value === this._cloudSpeed) return;
    this._cloudSpeed = value;
    this.updateSkyProperties();
  }

  @property({ type: 'number', min: 0, max: 1, step: 0.05, description: 'Cloud coverage amount (0 = no clouds)' })
  get cloudCoverage(): number {
    return this._cloudCoverage;
  }
  set cloudCoverage(value: number) {
    if (value === this._cloudCoverage) return;
    this._cloudCoverage = value;
    this.updateSkyProperties();
  }

  @property({ type: 'number', min: 0, max: 1, step: 0.05, description: 'Cloud density/opacity' })
  get cloudDensity(): number {
    return this._cloudDensity;
  }
  set cloudDensity(value: number) {
    if (value === this._cloudDensity) return;
    this._cloudDensity = value;
    this.updateSkyProperties();
  }

  @property({ type: 'number', min: 0, max: 1, step: 0.05, description: 'Cloud elevation height (0 = horizon, 1 = overhead)' })
  get cloudElevation(): number {
    return this._cloudElevation;
  }
  set cloudElevation(value: number) {
    if (value === this._cloudElevation) return;
    this._cloudElevation = value;
    this.updateSkyProperties();
  }

  @property({ type: 'boolean', description: 'Show sun disc (disable for environment map generation)' })
  get showSunDisc(): boolean {
    return this._showSunDisc;
  }
  set showSunDisc(value: boolean) {
    if (value === this._showSunDisc) return;
    this._showSunDisc = value;
    this.updateSkyProperties();
  }

  @property({ type: 'boolean', description: 'Whether to generate environment map from sky and apply it to the scene (default: false)' })
  get generateEnvironmentMap(): boolean {
    return this._generateEnvironmentMap;
  }
  set generateEnvironmentMap(value: boolean) {
    if (value === this._generateEnvironmentMap) return;
    this._generateEnvironmentMap = value;

    if (value) {
      if (this.isEditor) {
        this.generateAndApplyEnvironmentMap();
      }
    } else {
      this.disposeEnvironmentMap();
    }
  }

  @property({ type: 'number', min: 0, max: 10, step: 0.1, description: 'Intensity of the environment map' })
  get environmentMapIntensity(): number {
    return this._environmentMapIntensity;
  }
  set environmentMapIntensity(value: number) {
    if (value === this._environmentMapIntensity) return;
    this._environmentMapIntensity = value;
  }

  public override initialize(options?: SkyComponentOptions): void {
    super.initialize(options);
    this.turbidity = options?.turbidity ?? this.turbidity;
    this.rayleigh = options?.rayleigh ?? this.rayleigh;
    this.mieCoefficient = options?.mieCoefficient ?? this.mieCoefficient;
    this.mieDirectionalG = options?.mieDirectionalG ?? this.mieDirectionalG;
    this.cloudScale = options?.cloudScale ?? this.cloudScale;
    this.cloudSpeed = options?.cloudSpeed ?? this.cloudSpeed;
    this.cloudCoverage = options?.cloudCoverage ?? this.cloudCoverage;
    this.cloudDensity = options?.cloudDensity ?? this.cloudDensity;
    this.cloudElevation = options?.cloudElevation ?? this.cloudElevation;
    this.showSunDisc = options?.showSunDisc ?? this.showSunDisc;
    this.generateEnvironmentMap = options?.generateEnvironmentMap ?? this.generateEnvironmentMap;
    this.environmentMapIntensity = options?.environmentMapIntensity ?? this.environmentMapIntensity;
    if (options?.sunPosition) {
      this.sunPosition = options.sunPosition.clone();
    }
  }

  /**
   * Create the appropriate sky instance based on the renderer type.
   * WebGPU uses SkyMesh, WebGL uses Sky.
   */
  private createSkyInstance(): void {
    if (this.skyInstance) {
      return;
    }
    const world = this.getWorld();
    const isWebGPU = !!world?.getRenderer()?.asWebGPU();

    if (isWebGPU) {
      this.skyInstance = new SkyMesh();
    } else {
      this.skyInstance = new Sky();
    }
    this.add(this.skyInstance);
    this.skyInstance.setTransient(true);
    this.updateSkyProperties();
  }

  /**
   * Push component state onto the live sky instance.
   */
  private updateSkyProperties(): void {
    if (!this.skyInstance) return;

    if (this.skyInstance instanceof Sky) {
      const uniforms = this.skyInstance.material.uniforms;
      uniforms['turbidity'].value = this._turbidity;
      uniforms['rayleigh'].value = this._rayleigh;
      uniforms['mieCoefficient'].value = this._mieCoefficient;
      uniforms['mieDirectionalG'].value = this._mieDirectionalG;
      uniforms['cloudScale'].value = this._cloudScale;
      uniforms['cloudSpeed'].value = this._cloudSpeed;
      uniforms['cloudCoverage'].value = this._cloudCoverage;
      uniforms['cloudDensity'].value = this._cloudDensity;
      uniforms['cloudElevation'].value = this._cloudElevation;
      uniforms['showSunDisc'].value = this._showSunDisc ? 1 : 0;
    } else if (this.skyInstance instanceof SkyMesh) {
      this.skyInstance.turbidity.value = this._turbidity;
      this.skyInstance.rayleigh.value = this._rayleigh;
      this.skyInstance.mieCoefficient.value = this._mieCoefficient;
      this.skyInstance.mieDirectionalG.value = this._mieDirectionalG;
      this.skyInstance.cloudScale.value = this._cloudScale;
      this.skyInstance.cloudSpeed.value = this._cloudSpeed;
      this.skyInstance.cloudCoverage.value = this._cloudCoverage;
      this.skyInstance.cloudDensity.value = this._cloudDensity;
      this.skyInstance.cloudElevation.value = this._cloudElevation;
      this.skyInstance.showSunDisc.value = this._showSunDisc ? 1 : 0;
    }

    this.updateSunPosition();
  }

  /**
   * Dispose the sky instance and release resources.
   */
  private disposeSky(): void {
    if (!this.skyInstance) return;

    this.skyInstance.removeFromParent();

    if (this.skyInstance instanceof Sky) {
      this.skyInstance.material?.dispose?.();
    }

    this.skyInstance = null;
  }

  public override beginPlay(): void {
    super.beginPlay();
    this.createSkyInstance();
    setTimeout(() => {
      // add small delay to allow the sky to respond to the directional light in the tick
      this.generateAndApplyEnvironmentMap();
    }, 100);
    this.getScene()?.addSceneEnvironment(this);
  }

  public override endPlay(): void {
    this.getScene()?.removeSceneEnvironment(this);
    this.disposeEnvironmentMap();
    this.disposeSky();
    super.endPlay();
  }

  @callable({ label: 'Generate Environment Map'})
  public generateAndApplyEnvironmentMap(): void {
    if (!this.skyInstance) return;
    if (!this.generateEnvironmentMap) return;

    const world = this.getWorld();
    if (!world) return;

    const renderer = world.getRenderer();
    if (!renderer) return;

    const webGLRenderer = renderer.asWebGL();
    const webGPURenderer = renderer.asWebGPU();

    if (!webGLRenderer && !webGPURenderer) return;

    // Store original parent and visibility state
    const originalParent = this.skyInstance.parent;
    const originalVisible = this.skyInstance.visible;

    // Ensure clouds don't animate during capture for consistency
    const wasCloudSpeed = this._cloudSpeed;
    this._cloudSpeed = 0;
    this.updateSkyProperties();

    let texture: THREE.Texture | null = null;

    if (webGLRenderer) {
      // WebGL path
      this.environmentRenderTarget ??= new THREE.WebGLCubeRenderTarget(512, {
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
        colorSpace: THREE.LinearSRGBColorSpace,
        generateMipmaps: true,
        minFilter: THREE.LinearMipmapLinearFilter,
        magFilter: THREE.LinearFilter,
      });
    } else {
      // WebGPU path
      this.environmentRenderTarget ??= new CubeRenderTarget(512, {
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
        colorSpace: THREE.LinearSRGBColorSpace,
        generateMipmaps: true,
        minFilter: THREE.LinearMipmapLinearFilter,
        magFilter: THREE.LinearFilter,
      });
    }

    this.environmentCamera ??= new THREE.CubeCamera(0.1, 1000, this.environmentRenderTarget!);

    const tempScene = new GenesysScene();
    tempScene.add(this.skyInstance);
    this.skyInstance.visible = true;

    this.environmentCamera.position.set(0, 0, 0);
    this.environmentCamera.update(webGLRenderer ?? webGPURenderer!, tempScene);

    texture = this.environmentRenderTarget.texture;

    // Restore cloud animation
    this._cloudSpeed = wasCloudSpeed;
    this.updateSkyProperties();

    // Restore sky to original parent
    if (originalParent) {
      originalParent.add(this.skyInstance);
    }
    this.skyInstance.visible = originalVisible;

    // Apply as scene environment
    if (texture) {
      texture.mapping = THREE.CubeReflectionMapping;
      texture.format = THREE.RGBAFormat;
      texture.colorSpace = THREE.LinearSRGBColorSpace;
      texture.type = THREE.UnsignedByteType;
    }

    if (this.isEditor) {
      if (!this.debugEnvMapSphere) {
        // Create sphere with reflective material
        const geometry = new THREE.SphereGeometry(1, 32, 32);
        const material = new THREE.MeshStandardMaterial({
          color: 0xffffff,
          metalness: 1.0,
          roughness: 0.0,
          envMap: texture,
        });
        this.debugEnvMapSphere = new THREE.Mesh(geometry, material);
        this.debugEnvMapSphere.setTransient(true);
        this.debugEnvMapSphere.useAbsoluteScale = true;
        this.add(this.debugEnvMapSphere);
      } else {
        // Update existing sphere's env map
        (this.debugEnvMapSphere.material as THREE.MeshStandardMaterial).envMap = texture;
        (this.debugEnvMapSphere.material as THREE.MeshStandardMaterial).needsUpdate = true;
      }
    }
  }

  /**
   * Disposes the environment map resources and debug sphere.
   */
  private disposeEnvironmentMap(): void {
    if (this.environmentRenderTarget) {
      this.environmentRenderTarget.dispose();
      this.environmentRenderTarget = null;
    }
    this.environmentCamera = null;

    // Dispose debug sphere
    if (this.debugEnvMapSphere) {
      this.debugEnvMapSphere.removeFromParent();
      this.debugEnvMapSphere.geometry.dispose();
      (this.debugEnvMapSphere.material as THREE.Material).dispose();
      this.debugEnvMapSphere = null;
    }
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);
    this.updateSunLight();
  }

  public override tickInEditor(deltaTime: number): void {
    super.tickInEditor(deltaTime);
    this.updateSunLight();
  }

  private updateSunLight(): void {
    if (!this.skyInstance) return;
    if (!this._sunLight?.deref()) {
      const world = this.getWorld();
      const actors = world?.getActors() ?? [];
      for (const actor of actors) {
        for (const component of actor.getComponents(DirectionalLightComponent)) {
          if (component.isSunLight) {
            this._sunLight = new WeakRef(component);
            break;
          }
        }
      }
    }
    this.updateSunPosition();
  }

  private updateSunPosition(): void {
    if (!this.skyInstance) return;
    let sunPosition = this.sunPosition.clone();
    const sunLight = this._sunLight?.deref();
    if (sunLight) {
      sunLight.getWorldDirection(sunPosition);
      sunPosition.negate();
    }
    if (this.skyInstance instanceof Sky) {
      this.skyInstance.material.uniforms['sunPosition'].value.copy(sunPosition);
    } else if (this.skyInstance instanceof SkyMesh) {
      this.skyInstance.sunPosition.value.copy(sunPosition);
    }
  }

  private getScene(): GenesysScene | undefined {
    return this.getWorld()?.getRenderScene();
  }

  public getSceneEnvironmentData(): SceneEnvironmentData {
    return {
      sceneComponent: this,
      environment: this.environmentRenderTarget?.texture ?? null,
      environmentIntensity: this.environmentMapIntensity,
    };
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override onEditorAddToWorld(): void {
    super.onEditorAddToWorld();
    this.createSkyInstance();
    setTimeout(() => {
      this.generateAndApplyEnvironmentMap();
    }, 100);
    this.getScene()?.addSceneEnvironment(this);
  }

  public override onEditorRemoveFromWorld(): void {
    this.disposeEnvironmentMap();
    this.disposeSky();
    super.onEditorRemoveFromWorld();
    this.getScene()?.removeSceneEnvironment(this);
  }

  public override getEditorClassIcon(): string | null {
    return 'Icon_Skybox';
  }
}
